import React, { useMemo, useState } from 'react';
import {
    LayoutDashboard,
    Users,
    CreditCard,
    Flame,
    FileText,
    Globe,
    Calendar,
    CalendarDays,
    ChevronLeft,
    Gift,
    Shield,
    ChevronDown,
    ChevronRight,
    UserCircle,
    X,
    ImagePlus,
    Database,
    ClipboardList,
    LifeBuoy,
} from 'lucide-react';
import { PERMISSIONS } from './PermissionSelector';
import { useAuth } from '../../contexts/AuthContext';
import { useWebsiteConfig } from '../../hooks/useWebsiteConfig';

interface SidebarProps {
    activeTab: string;
    setActiveTab: (tab: string) => void;
    collapsed: boolean;
    setCollapsed: (collapsed: boolean) => void;
}

interface NavItem {
    id: string;
    label: string;
    icon: any;
    children?: NavItem[];
}

interface NavGroup {
    label: string;
    items: NavItem[];
}

export const Sidebar: React.FC<SidebarProps> = ({
    activeTab,
    setActiveTab,
    collapsed,
    setCollapsed
}) => {
    const { user } = useAuth();
    const { config, getImageUrl } = useWebsiteConfig();
    const [expandedItems, setExpandedItems] = useState<string[]>(['users']); // Users expanded by default

    const toggleExpand = (itemId: string) => {
        setExpandedItems(prev =>
            prev.includes(itemId)
                ? prev.filter(id => id !== itemId)
                : [...prev, itemId]
        );
    };

    const navigationGroups = useMemo(() => {
        const groups: NavGroup[] = [];

        // Helper to check permission
        const hasPermission = (permission: string) => {
            if (user?.role === 'super_admin') return true;
            if (user?.role === 'temple_admin') return false;
            return user?.permissions?.includes(permission) || false;
        };

        // --- Temple Admin View ---
        if (user?.role === 'temple_admin') {
            groups.push({
                label: 'Overview',
                items: [
                    { id: 'temple-dashboard', label: 'Dashboard', icon: LayoutDashboard }
                ]
            });
            groups.push({
                label: 'Operations',
                items: [
                    { id: 'poojas', label: 'Poojas', icon: Flame },
                    { id: 'pooja-bookings', label: 'Bookings', icon: Calendar },
                    { id: 'events', label: 'Events', icon: CalendarDays },
                    { id: 'event-bookings', label: 'Event Bookings', icon: Calendar },
                    { id: 'priests', label: 'Priests', icon: Users }
                ]
            });
            groups.push({
                label: 'Finance',
                items: [
                    { id: 'temple-donations', label: 'Donations', icon: CreditCard },
                    { id: 'temple-reports', label: 'Reports', icon: FileText }
                ]
            });
            groups.push({
                label: 'Legacy Data',
                items: [
                    {
                        id: 'legacy',
                        label: 'Old Data',
                        icon: Database,
                        children: [
                            { id: 'legacy-devotees', label: 'Devotees', icon: Users }
                        ]
                    }
                ]
            });
            return groups;
        }

        // --- Super Admin / Admin View ---

        // Overview Group
        groups.push({
            label: 'Main',
            items: [{ id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard }]
        });

        // Management Group
        const managementItems: NavItem[] = [];
        if (hasPermission(PERMISSIONS.VIEW_TEMPLES)) {
            managementItems.push(
                { id: 'temple-registrations', label: 'Temple Registrations', icon: FileText }
            );
        }
        if (hasPermission(PERMISSIONS.VIEW_USERS)) {
            // Users with dropdown
            managementItems.push({
                id: 'users',
                label: 'Users',
                icon: Users,
                children: [
                    { id: 'users-devotees', label: 'Devotees', icon: UserCircle },
                    { id: 'users-priests', label: 'Priests', icon: UserCircle },
                    { id: 'users-temple-admins', label: 'Temple Admins', icon: UserCircle },
                ]
            });
        }
        if (hasPermission(PERMISSIONS.VIEW_ADMINS) || user?.role === 'super_admin') {
            managementItems.push({ id: 'admins', label: 'Admins', icon: Shield });
        }
        if (managementItems.length > 0) {
            groups.push({ label: 'Management', items: managementItems });
        }

        // Services Group
        const servicesItems: NavItem[] = [];
        if (hasPermission(PERMISSIONS.VIEW_POOJAS)) {
            servicesItems.push(
                { id: 'poojas', label: 'Poojas', icon: Flame },
                { id: 'pooja-bookings', label: 'Bookings', icon: Calendar },
                { id: 'events', label: 'Events', icon: CalendarDays },
                { id: 'event-bookings', label: 'Event Bookings', icon: Calendar },
                { id: 'sponsorships', label: 'Sponsorships', icon: Gift },
                { id: 'sponsorship-bookings', label: 'Sponsorship Payments', icon: CreditCard },
                { id: 'sankalpam', label: 'Sankalpam', icon: ClipboardList },
                { id: 'tickets', label: 'Support Tickets', icon: LifeBuoy },
            );
        }
        if (servicesItems.length > 0) {
            groups.push({ label: 'Services', items: servicesItems });
        }

        // Finance Group
        const financeItems: NavItem[] = [];
        if (hasPermission(PERMISSIONS.VIEW_DONATIONS)) {
            financeItems.push({ id: 'donations', label: 'Donations', icon: CreditCard });
        }
        if (user?.role === 'super_admin') {
            financeItems.push({ id: 'donation-causes', label: 'Causes', icon: Gift });
        }
        if (financeItems.length > 0) {
            groups.push({ label: 'Finance', items: financeItems });
        }

        // Settings & Reports
        const configItems: NavItem[] = [];
        if (hasPermission(PERMISSIONS.MANAGE_REPORTS)) {
            configItems.push({ id: 'reports', label: 'Reports', icon: FileText });
        }
        if (hasPermission(PERMISSIONS.MANAGE_SETTINGS)) {
            configItems.push(
                { id: 'subscription-plans', label: 'Subscription Plans', icon: CreditCard },
                { id: 'website-config', label: 'Web Config', icon: Globe }
            );
        }
        if (user?.role === 'super_admin') {
            configItems.push({ id: 'hero-banners', label: 'Hero Banners', icon: ImagePlus });
        }
        if (configItems.length > 0) {
            groups.push({ label: 'Configuration', items: configItems });
        }

        // Legacy Data (Old records)
        groups.push({
            label: 'Legacy Data',
            items: [
                {
                    id: 'legacy',
                    label: 'Old Data',
                    icon: Database,
                    children: [
                        { id: 'legacy-data-import', label: 'Bulk Import', icon: Database },
                        { id: 'legacy-devotees', label: 'Devotees', icon: Users },
                    ]
                }
            ]
        });

        return groups;
    }, [user]);

    const handleNavigation = (itemId: string) => {
        setActiveTab(itemId);
        if (window.innerWidth < 768) {
            setCollapsed(false);
        }
    };

    const renderNavItem = (item: NavItem, level: number = 0) => {
        const Icon = item.icon;
        const isActive = activeTab === item.id;
        const hasChildren = item.children && item.children.length > 0;
        const isExpanded = expandedItems.includes(item.id);
        const isChildActive = item.children?.some(child => activeTab === child.id);

        return (
            <div key={item.id} className="mb-0.5">
                <button
                    onClick={() => {
                        if (hasChildren) {
                            toggleExpand(item.id);
                        } else {
                            handleNavigation(item.id);
                        }
                    }}
                    title={collapsed ? item.label : undefined}
                    className={`
                        w-full group flex items-center gap-3 px-3 py-2 text-[13px] font-medium rounded-lg transition-all duration-200 relative overflow-hidden
                        ${isActive || isChildActive
                            ? 'bg-[color-mix(in_srgb,var(--color-primary)_12%,white)] text-[var(--color-primary)]'
                            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'}
                        ${collapsed ? 'justify-start md:justify-center' : ''}
                        ${level > 0 ? 'ml-7' : ''}
                    `}
                >
                    {(isActive || isChildActive) && (
                        <div className={`absolute left-0 top-0 bottom-0 w-1 bg-[var(--color-primary)] rounded-r-lg ${collapsed ? 'block md:hidden' : 'block'}`}></div>
                    )}

                    <Icon
                        size={18}
                        className={`flex-shrink-0 transition-colors ${isActive || isChildActive ? 'text-[var(--color-primary)]' : 'text-gray-400 group-hover:text-gray-600'}`}
                        strokeWidth={isActive || isChildActive ? 2.5 : 2}
                    />
                    <span className={`truncate flex-1 text-left ${collapsed ? 'block md:hidden' : 'block'}`}>{item.label}</span>

                    {hasChildren && (
                        <div className={`${collapsed ? 'block md:hidden' : 'block'}`}>
                            {isExpanded ? <ChevronDown size={14} className="text-gray-400" /> : <ChevronRight size={14} className="text-gray-400" />}
                        </div>
                    )}
                </button>

                {/* Render children if expanded */}
                {hasChildren && isExpanded && (
                    <div className={`mt-0.5 space-y-0.5 ${collapsed ? 'block md:hidden' : 'block'}`}>
                        {item.children!.map(child => (
                            <button
                                key={child.id}
                                onClick={() => handleNavigation(child.id)}
                                className={`
                                    w-full group flex items-center gap-3 px-3 py-1.5 text-[12px] font-medium rounded-lg transition-all duration-200 pl-10 relative
                                    ${activeTab === child.id
                                        ? 'bg-[color-mix(in_srgb,var(--color-primary)_10%,white)] text-[var(--color-primary)]'
                                        : 'text-gray-500 hover:bg-gray-50 hover:text-gray-900'}
                                `}
                            >
                                <child.icon
                                    size={16}
                                    className={`flex-shrink-0 transition-colors ${activeTab === child.id ? 'text-[var(--color-primary)]' : 'text-gray-400 group-hover:text-gray-600'}`}
                                />
                                <span className="truncate flex-1 text-left">{child.label}</span>
                                {activeTab === child.id && (
                                    <div className="absolute left-4 w-1 h-1 rounded-full bg-[var(--color-primary)]"></div>
                                )}
                            </button>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    return (
        <>
            {/* Mobile Backdrop */}
            {collapsed && (
                <div
                    className="fixed inset-0 bg-gray-900/40 backdrop-blur-sm z-40 md:hidden transition-opacity"
                    onClick={() => setCollapsed(false)}
                />
            )}

            <aside
                className={`fixed left-0 top-0 h-screen shadow-xl md:shadow-sm border-r border-gray-200 z-50 transition-all duration-300 flex flex-col
                ${collapsed ? 'w-64 translate-x-0 md:w-20' : 'w-64 -translate-x-full md:translate-x-0 md:w-64'}
                `}
                style={{ backgroundColor: 'var(--sidebar-bg, #FFFFFF)' }}
            >
                {/* Sidebar Header */}
                <div className={`h-16 flex justify-between items-center px-4 border-b border-gray-100 shadow-sm shrink-0 ${collapsed ? 'justify-between md:justify-center' : 'justify-between'}`} style={{ backgroundColor: 'inherit' }}>
                    <div className={`flex items-center gap-3 overflow-hidden ${collapsed ? 'flex md:hidden' : 'flex'}`}>
                        {config?.logo ? (
                            <img src={getImageUrl(config.logo)} alt="Logo" className="h-12 w-auto object-contain" />
                        ) : (
                            <div className="w-8 h-8 rounded-lg flex items-center justify-center shadow-md shrink-0 bg-[linear-gradient(135deg,var(--color-primary),var(--color-tertiary))]">
                                <span className="text-white font-bold text-lg leading-none">F</span>
                            </div>
                        )}
                    </div>

                    <button
                        onClick={() => setCollapsed(!collapsed)}
                        className={`p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors ${collapsed ? 'block md:block' : 'block'}`}
                    >
                        <span className="hidden md:block">
                            {collapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
                        </span>
                        <span className="block md:hidden">
                            <X size={20} />
                        </span>
                    </button>
                </div>

                {/* Navigation */}
                <div className="flex-1 overflow-y-auto py-4 px-3 scrollbar-thin scrollbar-thumb-gray-200">
                    {navigationGroups.map((group, index) => (
                        <div key={index} className="mb-4">
                            <h3 className={`px-3 text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2 ${collapsed ? 'block md:hidden' : 'block'}`}>
                                {group.label}
                            </h3>
                            <div className="space-y-0.5">
                                {group.items.map((item) => renderNavItem(item))}
                            </div>
                        </div>
                    ))}
                </div>

                {/* User Profile Mini */}
                <div className="p-3 border-t border-gray-100 shrink-0" style={{ background: 'color-mix(in srgb, var(--sidebar-bg, #FFFFFF), black 3%)' }}>
                    <div className={`flex items-center gap-3 ${collapsed ? 'justify-start md:justify-center' : 'justify-start'}`}>
                        <div className="h-8 w-8 rounded-full flex items-center justify-center text-white font-bold text-sm shrink-0 shadow-sm border border-[color-mix(in_srgb,var(--color-primary)_30%,white)] bg-[linear-gradient(135deg,var(--color-primary),var(--color-tertiary))]">
                            {user?.name?.charAt(0).toUpperCase()}
                        </div>
                        <div className={`overflow-hidden flex-1 ${collapsed ? 'block md:hidden' : 'block'}`}>
                            <p className="text-[13px] font-semibold text-gray-900 truncate">{user?.name}</p>
                            <p className="text-[11px] text-gray-500 truncate capitalize font-medium">{user?.role?.replace('_', ' ')}</p>
                        </div>
                    </div>
                </div>
            </aside>
        </>
    );
};
